import { defineStore } from 'pinia';
import _ from 'lodash';

import GoogleTagManagerApi from '@/extensions/marketing/store/api/googleTagManager';
import { useSetupStore } from '@/stores';

const defaultForm = {
    id: null,
    container_id: '',
    is_enabled: false,
    send_user_data: false,
    track_page_view: true,
    track_view_item: true,
    track_view_item_list: true,
    track_select_item: true,
    track_add_to_cart: true,
    track_remove_from_cart: true,
    track_view_cart: true,
    track_begin_checkout: true,
    track_add_shipping_info: true,
    track_add_payment_info: true,
    track_purchase: true,
    track_search: true,
    cms: null,
};

const defaultRules = {
    // Shape only, no length: the sources disagree on whether the suffix is 6, 7 or
    // 8 characters and Google has lengthened it before, so a tight bound here would
    // lock a tenant out of a valid container. The server enforces a sane range.
    // (A {6,10} quantifier could not be used anyway - vee-validate splits string rule
    // params on commas, so it would reach the regex rule cut in half.)
    container_id: 'required|regex:^GTM-[A-Z0-9]+$',
};

export const useMarketingGoogleTagManagerStore = defineStore('marketingGoogleTagManager', {
    state: () => ({
        loading: true,
        form: _.cloneDeep(defaultForm),
        rules: _.cloneDeep(defaultRules),
    }),

    actions: {
        setForm(data) {
            this.form = _.cloneDeep(data);
        },

        // Merging onto a clone of defaultForm keeps the form shape complete even
        // when the API returns a blank (firstOrNew) record, and the null -> ''
        // coercion keeps Element inputs from choking on a null model.
        normalize(data) {
            const normalized = _.cloneDeep(defaultForm);
            _.assign(normalized, data);
            if (normalized.container_id === null) normalized.container_id = '';
            return normalized;
        },

        async fetch() {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            if (!cms) return;

            this.loading = true;
            return GoogleTagManagerApi.fetch(cms.id)
                .then(data => {
                    this.form = this.normalize(data);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        async save() {
            const setupStore = useSetupStore();
            const cms = setupStore.activeCms;
            if (!cms) return;

            const payload = _.cloneDeep(this.form);
            payload.cms = { id: cms.id };

            return GoogleTagManagerApi.save(payload).then(data => {
                this.form = this.normalize(data);
            });
        },
    },
});
